import { watch } from 'vue'
import { useEventListener } from '@vueuse/core'
import { useAuthStore } from '@/stores/authStore.js'
import { authChannel, AuthChannelMessage } from '@/composables/useAuthChannel.js'
import router from '@/router/router.js'
import { AuthGuard } from '@/router/guards/AuthGuard.js'

/**
 * Guards that mean the current route is showing authenticated content, so a logout elsewhere must log out this page.
 * Everything else (login, registration, error pages) is already
 * a safe place to be sitting when another tab signs out.
 */
const REQUIRES_SESSION = [
	AuthGuard.RequireLoggedIn,
	AuthGuard.RequireAdmin,
	AuthGuard.RequireDataUpload,
	AuthGuard.RequireDocumentUpload,
	AuthGuard.RequireDocumentDownload,
	AuthGuard.RequirePermissions
]

/**
 * Guards that mean the current route must not be sitting there once a session exists, so a login
 * elsewhere must move this page. The mirror of REQUIRES_SESSION.
 */
const REQUIRES_GUEST = [
	AuthGuard.RequireLoggedOut
]

/**
 * Guest-only pages that carry no guard to move them on reload. /auth/reset-password is
 * NoEnforcement in the router, but its POST goes through the gateway's 'guest' middleware and so
 * 403s "Unexpected auth state." once another tab has signed in. Reloading would re-render the same
 * dead page, so these are sent to the dashboard explicitly.
 */
const GUEST_ONLY_ROUTES = ['auth.reset-password']

/**
 * Keeps every open tab in step when one of them logs out or logs in.
 *
 * The login half exists because localStorage is shared: the moment one tab signs in, useLocalStorage
 * hydrates every other tab's authStore with the new token, so a tab left sitting on the login page
 * starts sending an Authorization header on its own login POST and the gateway's guest middleware
 * rejects it with "Unexpected auth state." Getting those tabs off the unauthenticated page closes
 * that window.
 *
 * Two signals, because either one alone has a gap:
 *  - the 'Auth' broadcast channel, posted by authStore.logout() and by a successful
 *    handleLoginResult(), is the explicit one
 *  - a native 'storage' event on the JWT key is the fallback, covering a browser without
 *    BroadcastChannel support, a dropped message, or the token being cleared by something other
 *    than logout() (devtools, "clear site data")
 *
 * Neither signal reaches the tab that caused it - a BroadcastChannel does not deliver to the context
 * that posted, and a 'storage' event never fires in the tab that wrote the change - so the tab doing
 * the logging out or in runs its own normal path and needs no suppression flag here.
 *
 * Note this cannot key off authStore.isLoggedIn: @vueuse's useLocalStorage registers its own
 * 'storage' listener when the store is created, well before this one, so by the time either signal
 * is handled the token has usually already been nulled out from under us. Whether this tab is
 * sitting on an authenticated route is the reliable question to ask instead.
 *
 * Call once, from App.vue.
 */
export function useAuthSync()
{
	const authStore = useAuthStore()
	let handled = false

	/**
	 * Called when a broadcast message or the storage event is caught.
	 */
	const onRemoteLogout = () =>
	{
		// Both signals usually fire for the same logout; only the first one should act.
		if (handled)
		{
			return
		}

		if (!REQUIRES_SESSION.includes(router.currentRoute.value?.meta?.authGuard))
		{
			return
		}

		handled = true
		authStore.handleRemoteLogout()
	}

	/**
	 * Called when a login happens in another tab. This tab's store has already been hydrated with the
	 * new token by useLocalStorage's own 'storage' listener, so there is nothing local to clear -
	 * clearing would null the token the other tab just wrote and sign it out. All that is left is to
	 * get off the unauthenticated page, which would otherwise 403 on its next guest-middleware call.
	 */
	const onRemoteLogin = () =>
	{
		if (handled)
		{
			return
		}

		const route = router.currentRoute.value
		const isGuestOnlyRoute = GUEST_ONLY_ROUTES.includes(route?.name)

		if (!REQUIRES_GUEST.includes(route?.meta?.authGuard) && !isGuestOnlyRoute)
		{
			return
		}

		handled = true

		if (isGuestOnlyRoute)
		{
			window.location.assign(router.resolve({ name: 'dashboard' }).href)
			return
		}

		// A plain reload rather than a navigation: requireLoggedOutGuard already knows where a signed-in
		// visitor to a guest route belongs, and a full load drops anything left over from the guest page
		// (half-typed credentials, in-flight requests). The login page's own onMounted logout(false)
		// never re-runs, because that guard redirects before LoginPage mounts.
		window.location.reload()
	}

	watch(authChannel.data, (message) =>
	{
		switch (message?.type)
		{
			case AuthChannelMessage.Logout:
				onRemoteLogout()
				break
			case AuthChannelMessage.Login:
				onRemoteLogin()
				break
		}
	})

	useEventListener(window, 'storage', (event) =>
	{
		if (event.key !== 'authStoreJwt')
		{
			return
		}

		// The token going away is a logout; a token appearing where there was none is a login. Testing
		// oldValue too keeps a token refresh (refreshUserData rewrites the key while still signed in)
		// from reading as a fresh login.
		if (event.newValue === null)
		{
			onRemoteLogout()
		}
		else if (event.oldValue === null)
		{
			onRemoteLogin()
		}
	})
}
